/**
 * Auth State Reducer
 *
 * Manages session state transitions using useReducer pattern.
 * Provides predictable state machine for authentication flow.
 */

import { isGlobalSessionExpired } from '@api-client/api-client';
import { AuthSessionState, AuthReducerState, AuthAction } from './authTypes';

// =============================================================================
// INITIAL STATE
// =============================================================================

export const initialAuthState: AuthReducerState = {
  sessionState: AuthSessionState.INITIALIZING,
};

// =============================================================================
// REDUCER
// =============================================================================

/**
 * Auth state reducer
 * Handles session state transitions with predictable state machine behavior
 */
export function authReducer(state: AuthReducerState, action: AuthAction): AuthReducerState {
  switch (action.type) {
    case 'SET_AUTHENTICATED':
      return { ...state, sessionState: AuthSessionState.AUTHENTICATED };

    case 'SET_UNAUTHENTICATED':
      return { ...state, sessionState: AuthSessionState.UNAUTHENTICATED };

    case 'SET_REAUTHENTICATING':
      return { ...state, sessionState: AuthSessionState.REAUTHENTICATING };

    case 'SET_SESSION_EXPIRED':
      return { ...state, sessionState: AuthSessionState.SESSION_EXPIRED };

    case 'SET_STATE':
      return { ...state, sessionState: action.payload };

    default:
      return state;
  }
}

// =============================================================================
// SELECTORS
// =============================================================================

/**
 * Check if session is blocked (expired or reauthenticating)
 * Used to prevent API calls during session recovery
 */
export function selectIsSessionBlocked(state: AuthReducerState): boolean {
  return (
    isGlobalSessionExpired() ||
    state.sessionState === AuthSessionState.SESSION_EXPIRED ||
    state.sessionState === AuthSessionState.REAUTHENTICATING
  );
}

/**
 * Check if user is fully authenticated and session is active
 */
export function selectIsAuthenticatedAndActive(
  state: AuthReducerState,
  logtoIsAuthenticated: boolean,
): boolean {
  return logtoIsAuthenticated && state.sessionState === AuthSessionState.AUTHENTICATED;
}

/**
 * Check if proactive refresh should be skipped
 */
export function selectShouldSkipProactiveRefresh(
  state: AuthReducerState,
  logtoIsAuthenticated: boolean,
): boolean {
  return (
    !logtoIsAuthenticated ||
    state.sessionState === AuthSessionState.SESSION_EXPIRED ||
    state.sessionState === AuthSessionState.REAUTHENTICATING ||
    state.sessionState === AuthSessionState.UNAUTHENTICATED
  );
}

// =============================================================================
// SESSION STATE CHECKER FACTORY
// =============================================================================

/**
 * Creates a session state checker bound to refs.
 * Returns functions to check common auth conditions.
 *
 * AIDEV-NOTE: This factory is used for callbacks that need synchronous access
 * to the latest state without waiting for React re-render. The refs are updated
 * synchronously during render, so they're always current.
 */
export const createSessionStateCheckers = (
  sessionStateRef: { current: AuthSessionState },
  logtoIsAuthenticatedRef: { current: boolean },
) => ({
  /** Check if session is blocked (expired or reauthenticating) */
  isSessionBlocked: (): boolean =>
    isGlobalSessionExpired() ||
    sessionStateRef.current === AuthSessionState.SESSION_EXPIRED ||
    sessionStateRef.current === AuthSessionState.REAUTHENTICATING,

  /** Check if user is fully authenticated and session is active */
  isAuthenticatedAndActive: (): boolean =>
    logtoIsAuthenticatedRef.current && sessionStateRef.current === AuthSessionState.AUTHENTICATED,

  /** Check if proactive refresh should be skipped */
  shouldSkipProactiveRefresh: (): boolean =>
    !logtoIsAuthenticatedRef.current ||
    sessionStateRef.current === AuthSessionState.SESSION_EXPIRED ||
    sessionStateRef.current === AuthSessionState.REAUTHENTICATING ||
    sessionStateRef.current === AuthSessionState.UNAUTHENTICATED,
});
